import React from 'react';
import classNames from 'classnames';
import { AppList, baseRealtyUrl, getUrlWithUtmQueryString, Button } from '@capcenter/shared';
import { ScheduleTourDateProps, ScheduleTourUrlProps } from '../../../interfaces/interfaces';
import DropdownSelect from '../../Shared/DropdownButton/DropdownSelect';
import DatePicker from './DatePicker';

import './ScheduleATour.scss';

interface TourTimesProps {
	text: string;
	value: string;
	index: number;
}

const TourTimes: TourTimesProps[] = [
	{
		text: 'Morning: 9am - 12pm',
		value: 'Morning',
		index: 0,
	},
	{
		text: 'Noon: 12pm - 3pm',
		value: 'Noon',
		index: 1,
	},
	{
		text: 'Afternoon: 3pm - 6pm',
		value: 'Afternoon',
		index: 2,
	},
	{
		text: 'Evening: 6pm - 9pm',
		value: 'Evening',
		index: 3,
	},
];

const TourTime = (props: {
	selectedCardIndex: number | undefined;
	selectedTime: TourTimesProps | undefined;
	setSelectedTime: (value: TourTimesProps) => void;
	currentTimeslot: number | undefined;
	setShowErrorMessage: (value: boolean) => void;
}) => {
	return (
		<div className="w-100 mt-3 px-4">
			<DropdownSelect
				id="scheduleTourTimePicker"
				defaultValue={props.selectedTime ? props.selectedTime.text : 'Select a time'}
				label="Tour Time"
				labelClass="mb-1"
				color="gray"
				variant="outline"
				resetText
				wrapperClass="schedule-tour__time-select-wrapper w-100"
				className="schedule-tour__time-select-button w-100 fw-bold"
				options={TourTimes.map((timeslot: TourTimesProps) => {
					return (
						<li
							tabIndex={0}
							key={timeslot.index}
							onClick={() => {
								props.setSelectedTime(timeslot);
								props.setShowErrorMessage(false);
							}}
							onKeyDown={(event: React.KeyboardEvent) => {
								if (event.key === 'Enter') {
									props.setSelectedTime(timeslot);
									props.setShowErrorMessage(false);
								}
							}}
							className={classNames(
								props.selectedCardIndex === 0 &&
								// First avail. option = 'Afternoon' (index 2) today
								(props.currentTimeslot === 0 && timeslot.index < 2
									? 'disabled'
									: // First avail. option = 'Evening' (index 3) today

									props.currentTimeslot === 1 && timeslot.index < 3
										? 'disabled'
										: // Don't restrict if timeslot = 2 as next avail. option = 'Morning' (index 0) tomorrow

										// First avail. option = 'Noon' (index 1) tomorrow
										props.currentTimeslot === 3 && timeslot.index < 1
											? 'disabled'
											: ''),
								'schedule-tour__time-option d-flex align-items-center cursor--pointer dropdown-item',
							)}
						>
							{timeslot.text}
						</li>
					);
				})}
			/>
		</div>
	);
};

const ScheduleATour = (props: { address: any, price: number}) => {
	const [selectedTime, setSelectedTime] = React.useState<TourTimesProps>();
	const [leftArrowActive, setLeftArrowActive] = React.useState(false);
	const [rightArrowActive, setRightArrowActive] = React.useState(true);
	const [selectedDate, setSelectedDate] = React.useState<ScheduleTourDateProps>();
	const [showDateErrorMessage, setShowDateErrorMessage] = React.useState(false);
	const [showTimeErrorMessage, setShowTimeErrorMessage] = React.useState(false);
	const [currentTimeslot, setCurrentTimeslot] = React.useState<number | undefined>();
	const [selectedCardIndex, setSelectedCardIndex] = React.useState<number | undefined>();

	const tourUrl: ScheduleTourUrlProps = {
		// baseUrl: `http://localhost:3001${AppList.scheduleTour.url}?`,
		baseUrl: `${baseRealtyUrl}${AppList.scheduleTour.url}?`,
		address: props.address,
		date: `${selectedDate?.year}-${selectedDate?.monthNumeric}-${selectedDate?.day}`, // ISO format
		time: `${selectedTime?.value}`,
		price: props.price
	};

	function handleCTAClick() {
		const urlToPush = getUrlWithUtmQueryString(encodeURI(
			tourUrl.baseUrl +
			`listingaddressfortour=${tourUrl.address}` +
			`&tourDate=${tourUrl.date}` +
			`&tourTime=${tourUrl.time}` +
			`&purchasePrice=${tourUrl.price}`,
		));

		if (selectedDate === undefined) {
			setShowDateErrorMessage(true);
		} else if (selectedTime === undefined) {
			setShowTimeErrorMessage(true);
		} else {
			window.open(`${urlToPush}`);
		}
	}

	// set current 'timeslot' to control which dates/times are shown
	React.useEffect(() => {
		const date = new Date();
		const currentHour = date.getHours();
		const currentMin = date.getMinutes();

		// timeslot[0] ('Morning')
		if (
			currentHour >= 21 || // on or after 21:00 // OR
			(currentHour <= 11 && currentMin <= 59) // on or before 11:59
		) {
			setCurrentTimeslot(0);
		}
		// timeslot[1] ('Noon')
		else if (
			currentHour >= 12 && // on or after 12:00 // AND
			currentHour <= 14 &&
			currentMin <= 59 // on or before 14:59
		) {
			setCurrentTimeslot(1);
		}
		// timeslot[2] ('Afternoon')
		else if (
			currentHour >= 15 && // on or after 15:00 // AND
			currentHour <= 17 &&
			currentMin <= 59 // on or before 17:59
		) {
			setCurrentTimeslot(2);
		}
		// timeslot[3] ('Evening')
		else if (
			currentHour >= 18 && // on or after 18:00 // AND
			currentHour <= 20 &&
			currentMin <= 59 // on or before 20:59
		) {
			setCurrentTimeslot(3);
		}
	}, [currentTimeslot]);

	return (
		<>
			<div className="schedule-tour__wrapper position-relative">
				<h3 id="scheduleTourComponentTitle" className="text-center s-3 px-4">Schedule a Tour</h3>
				<DatePicker
					hideArrows
					leftArrowActive={leftArrowActive}
					setLeftArrowActive={setLeftArrowActive}
					rightArrowActive={rightArrowActive}
					setRightArrowActive={setRightArrowActive}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					setShowErrorMessage={setShowDateErrorMessage}
					currentTimeslot={currentTimeslot}
					selectedCardIndex={selectedCardIndex}
					setSelectedCardIndex={setSelectedCardIndex}
				/>

				{showDateErrorMessage && (
					<div
						className={classNames(
							showDateErrorMessage && 'show',
							'schedule-tour__error-message w-100 cc-color--negative small fw-bold px-4 py-1',
						)}
					>
					Please select a date above.
					</div>
				)}

				<TourTime
					selectedCardIndex={selectedCardIndex}
					selectedTime={selectedTime}
					setSelectedTime={setSelectedTime}
					currentTimeslot={currentTimeslot}
					setShowErrorMessage={setShowTimeErrorMessage}
				/>

				{showTimeErrorMessage && (
					<div
						className={classNames(
							showTimeErrorMessage && 'show',
							'schedule-tour__error-message w-100 cc-color--negative small fw-bold px-4 py-1',
						)}
					>
					Please select a time above.
					</div>
				)}

			</div>
			<Button
				variant="filled"
				label="Request Tour Time"
				icon="far fa-house-user"
				className="w-100 fw-bold mt-4"
				size="lg"
				resetText
				onClick={() => handleCTAClick()}
			/>
		</>
	);
};

export default ScheduleATour;
